import { Grid } from '@mui/material';
import Box from '@mui/material/Box';
import Button from 'common/Button';
import ButtonSecondary from 'common/ButtonSecondary';
import FormInputLabel from 'common/FormInputLabel';
import FormModal from 'common/FormModal';
import FormStyle from 'common/FormStyle';
import FormTextAreaInput from 'common/FormTextAreaInput';
import FormTextInput from 'common/FormTextInput';
import StickyActionFooter from 'common/StickyActionFooter';
import { AttachMoney } from '@mui/icons-material';
import { useAuth } from 'context/AuthContext';
import supabaseHandler from 'utils/SupaBase';
import { toasterNotification } from 'utils/toaster';

import React, { useEffect, useState } from 'react';

const ServicePopup = ({ open, onClose, serviceData, getServiceList, businessId, handleDelete, onServiceAdded }) => {
    const { token } = useAuth();
    const [apiResponse, setApiResponse] = useState();
    const [apiError, setApiError] = useState();
    const [loading, setLoading] = useState(false);
    const [initialFormState, setInitialFormState] = useState(null);
    const [attemptedSubmit, setAttemptedSubmit] = useState(false);
    const [error, setError] = useState('');
    
    const [formData, setFormData] = useState({
        name: serviceData?.row?.name || serviceData?.name || '',
        description: serviceData?.row?.description || serviceData?.description || '',
        exclude_FMP: false,
        isBookable: (serviceData?.row?.isBookable !== undefined ? serviceData?.row?.isBookable : serviceData?.isBookable !== undefined ? serviceData?.isBookable : true),
        price: (serviceData?.row?.price !== null && serviceData?.row?.price !== undefined ? serviceData?.row?.price : serviceData?.price !== null && serviceData?.price !== undefined ? serviceData?.price : ''),
    });

    useEffect(() => {
        if (open) {
            setAttemptedSubmit(false); // Reset submission attempt
            setError(''); // Clear any errors
            setLoading(false); // Reset loading state
        }
    }, [open]);

    // Currency formatting functions (same as invoice form)
    const handlePriceChange = (e) => {
        const { value } = e.target;
        
        // Remove all non-numeric characters
        const numericValue = value.replace(/[^0-9]/g, '');
        
        // Format as currency: convert to cents, then format back
        let finalValue = '';
        if (numericValue) {
            // Convert to number and divide by 100 to get dollars
            const cents = parseInt(numericValue, 10);
            const dollars = cents / 100;
            finalValue = dollars.toFixed(2);
        }
        
        setFormData({ ...formData, price: finalValue });
    };

    const handlePriceFocus = (e) => {
        // If the value is empty, set it to "0.00" and select it for easy editing
        if (!formData.price || formData.price === '') {
            setFormData({ ...formData, price: '0.00' });
            setTimeout(() => {
                e.target.select();
            }, 0);
        } else if (formData.price === '0.00') {
            setTimeout(() => {
                e.target.select();
            }, 0);
        }
    };

    // Function to validate if mandatory fields are filled (price > 1 is validated on submit)
    const isFormValid = () => {
        return !!formData.name?.trim() && (formData.price !== null && formData.price !== undefined && formData.price !== '');
    };

    const handleClose = () => {
        const hasUnsavedChanges = JSON.stringify(formData) !== initialFormState;

        if (hasUnsavedChanges) {
            const confirmExit = window.confirm('You have unsaved changes. Do you really want to close?');
            if (!confirmExit) return;
        }

        onClose();
    };

    const handleDeleteService = async () => {
        if (serviceData && handleDelete) {
            // Handle both data structures: serviceData.row (from other components) and serviceData (direct from ServiceTable)
            const rowData = serviceData?.row || serviceData;
            await handleDelete(rowData);
            handleClose();
        }
    };
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };
    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setFormData({ ...formData, [name]: checked });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setAttemptedSubmit(true);
        
        if (!isFormValid()) {
            setError('Please fill in all required fields.');
            return;
        }
        
        setLoading(true);
       
        let priceValue = null;
        if (formData?.price !== null && formData?.price !== undefined && formData?.price !== '') {
            const parsedPrice = parseFloat(formData.price);
            if (isNaN(parsedPrice)) {
                setError('Please enter a valid price.');
                setLoading(false);
                return;
            }
            if (parsedPrice <= 1) {
                toasterNotification('info', '', 'Service price must be greater than 1.');
                setLoading(false);
                return;
            }
            priceValue = parsedPrice;
        }

        // Get business_id from prop or serviceData (for edit mode)
        const finalBusinessId = businessId || serviceData?.row?.business_id || serviceData?.business_id;

        // Validate business_id
        if (!finalBusinessId) {
            setError('Business ID is required.');
            setLoading(false);
            return;
        }

        try {
            // Validate access token
            if (!token) {
                setError('No authentication token found. Please try again.');
                setLoading(false);
                return;
            }

            // Determine if we're adding or editing
            const serviceId = serviceData?.row?.id || serviceData?.id;
            const isEditMode = !!serviceId;

            // Build payload
            const payload = {
                name: formData.name,
                description: formData.description || '', // Backend requires description
                business_id: finalBusinessId,
                exclude_FMP: formData?.exclude_FMP || false,
                isBookable: formData?.isBookable !== undefined ? formData.isBookable : true,
                price: priceValue,
            };

            // Add id for update operation
            if (isEditMode) {
                payload.id = serviceId;
            }

            // Determine the appropriate API endpoint and method
            const apiEndpoint = isEditMode 
                ? `${process.env.REACT_APP_SERVER_URL}/api/providers/update-service`
                : `${process.env.REACT_APP_SERVER_URL}/api/providers/add-service`;
            const method = isEditMode ? 'PUT' : 'POST';

            // Make API call to backend
            const response = await fetch(
                apiEndpoint,
                {
                    method: method,
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(payload)
                }
            );

            const result = await response.json();

            if (!response.ok || result.error) {
                const errorMessage = result.error || (isEditMode ? 'Failed to update service. Please try again.' : 'Failed to add service. Please try again.');
                setError(errorMessage);
                setApiError(result.error);
                setLoading(false);
                return;
            }

            // Backend returns { success: true, data: data, message: '...' }
            if (result.success && result.data) {
                setApiResponse(result.data);
                handleSuccess(result.data);
            } else {
                // Fallback if response format is unexpected
                console.warn('Unexpected response format from service API');
                setError('Unexpected response from server. Please try again.');
                setLoading(false);
            }
        } catch (err) {
            console.error("Unexpected error:", err);
            setError('An unexpected error occurred. Please try again.');
            setLoading(false);
        }
    };

    const handleSuccess = (responseData) => {
        // If this is a new service (not editing), call onServiceAdded with the service data
        if (!serviceData && responseData && onServiceAdded) {
            // Backend returns data as an array from Supabase insert/select, get the first item
            const newService = Array.isArray(responseData) ? responseData[0] : responseData;
            onServiceAdded(newService);
        }
        
        onClose();
        getServiceList().then();
    };

    useEffect(() => {
        // Only update form data when popup is open
        if (!open) return;
        
        let newFormData;
        
        // Helper function to format price with .00 cents
        const formatPrice = (price) => {
            if (price !== null && price !== undefined && price !== '') {
                const numPrice = parseFloat(price);
                if (!isNaN(numPrice)) {
                    return numPrice.toFixed(2);
                }
            }
            return '';
        };
        
        if (serviceData?.row) {
            newFormData = {
                name: serviceData.row.name || '',
                description: serviceData.row.description || '',
                exclude_FMP: serviceData.row.exclude_FMP || false,
                isBookable: serviceData.row.isBookable !== undefined ? serviceData.row.isBookable : true,
                price: formatPrice(serviceData.row.price),
            };
        } else if (serviceData) {
            newFormData = {
                name: serviceData.name || '',
                description: serviceData.description || '',
                exclude_FMP: serviceData.exclude_FMP || false,
                isBookable: serviceData.isBookable !== undefined ? serviceData.isBookable : true,
                price: formatPrice(serviceData.price),
            };
        } else {
            // Fresh state for new service
            newFormData = {
                name: '',
                description: '',
                price: '',
                isBookable: true,
                exclude_FMP: false,
            };
        }
        
        setFormData(newFormData);
        
        // Set initial form state after loading the actual data
        setInitialFormState(JSON.stringify(newFormData));
    }, [serviceData, open]);



    return (
        <FormModal 
            open={open} 
            onClose={handleClose} 
            title={serviceData ? 'Edit Service' : 'Add a Service'}
            subText={serviceData ? 'Update your service information below.' : 'Add a new service to your business profile. Set pricing and configure whether patients can book this service online.'}
            stickyFooter={
                <StickyActionFooter
                    onCancel={handleClose}
                    onSave={handleSubmit}
                    cancelText="Cancel"
                    saveText={serviceData ? "Save Changes" : "Add Service"}
                    isLoading={loading}
                    disabled={!isFormValid()}
                    showDelete={!!serviceData}
                    onDelete={handleDeleteService}
                    deleteText="Delete Service"
                />
            }
        >
            <FormStyle onSubmit={handleSubmit}>
                {error && <div className="text-red-500 mb-4">{error}</div>}
                <Grid container spacing={2}>
                    <FormTextInput
                        formData={formData}
                        label="Service Name"
                        name="name"
                        handleChange={handleChange}
                        placeholder="Enter your service name"
                        required={true}
                        labelSize={12}
                        textSize={12}
                    />

                    <FormTextInput
                        formData={formData}
                        label="Service Price"
                        value={formData.price ?? ""}
                        name="price"
                        handleChange={handlePriceChange}
                        placeholder="0.00"
                        required={true}
                        labelSize={12}
                        textSize={12}
                        type="number"
                        inputProps={{
                            step: "0.01",
                            min: "0",
                            inputMode: "decimal",
                            style: { textAlign: 'right' },
                            onFocus: handlePriceFocus,
                            onKeyDown: (ev) => {
                                const allowed = ['Backspace','Delete','ArrowLeft','ArrowRight','Tab','Home','End'];
                                if (allowed.includes(ev.key)) return;
                                const isDigit = /[0-9]/.test(ev.key);
                                if (!isDigit) {
                                    ev.preventDefault();
                                    return;
                                }
                            },
                            onPaste: (ev) => {
                                const paste = (ev.clipboardData || window.clipboardData).getData('text');
                                if (!/^\d*$/.test(paste)) {
                                    ev.preventDefault();
                                }
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <Box
                                    sx={{
                                        backgroundColor: 'rgba(203, 193, 183, 1)',
                                        color: 'rgba(87, 83, 78, 1)',
                                        height: '42px',
                                        width: '42px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginLeft: '-14px',
                                        borderTopLeftRadius: '7px',
                                        borderBottomLeftRadius: '7px',
                                    }}
                                >
                                    <AttachMoney fontSize="small" />
                                </Box>
                            ),
                            sx: {
                                '& input[type=number]': {
                                    MozAppearance: 'textfield',
                                },
                                '& input[type=number]::-webkit-outer-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                },
                                '& input[type=number]::-webkit-inner-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                },
                                '& .MuiInputBase-input': {
                                    paddingRight: '20px !important',
                                },
                            }
                        }}
                    />

                    <FormTextAreaInput
                        label={'Service Description'}
                        name={'description'}
                        value={formData.description}
                        labelSize={12}
                        textSize={12}
                        handleChange={handleChange}
                        placeholder={'Enter a description for you service'}
                        required={false}
                        style={{ marginBottom: '0px' }}
                    />

                    <Grid item xs={12}>
                        <div style={{ 
                            borderTop: '1px solid #e5e7eb', 
                            marginTop: '24px', 
                            marginBottom: '24px' 
                        }}></div>
                    </Grid>

                    <FormInputLabel label={'Service Availability'} labelBottomMargin="mb-0" />
                    <span className="text-xs text-[#999] pl-4">
                        Use the 'Available for Purchase' checkbox to control whether this service can be purchased—when checked, patients can purchase the service; when unchecked, the service and price are shown but cannot be purchased.
                    </span>

                    <div className="flex gap-2 mt-3 pl-4" style={{ alignItems: 'center' }}>
                        <div 
                            className="cursor-pointer"
                            onClick={() => handleCheckboxChange({ target: { name: 'isBookable', checked: !formData.isBookable } })}
                            style={{
                                width: '16px',
                                height: '16px',
                                borderRadius: '4px',
                                border: formData.isBookable ? '1px solid #75BD2F' : '1px solid #cbc1b7',
                                backgroundColor: formData.isBookable ? '#75BD2F' : '#f5f1ed',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: 0,
                                transition: 'all 0.3s ease'
                            }}
                        >
                            {formData.isBookable && (
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="white" stroke="white" strokeWidth="1.5" />
                                </svg>
                            )}
                        </div>
                        <label 
                            onClick={() => handleCheckboxChange({ target: { name: 'isBookable', checked: !formData.isBookable } })}
                            style={{ fontSize: '14px', color: '#3E3E4A', fontFamily: 'Poppins, sans-serif', fontWeight: '500', margin: 0, cursor: 'pointer' }}
                        >
                            Available for Purchase
                        </label>
                    </div>

                    <Grid item xs={12}>
                        <div style={{ 
                            borderTop: '1px solid #e5e7eb', 
                            marginTop: '24px', 
                            marginBottom: '24px' 
                        }}></div>
                    </Grid>

                    <FormInputLabel label={'Federal Medical Programs'} labelBottomMargin="mb-0" />
                    <span className="text-xs text-[#999] pl-4">
                        Check the box below to add an additional disclosure to the patient's booking confirmation screen
                        to exclude this service from Federal Medical Programs.
                    </span>

                    <div className="flex gap-2 mt-3 pl-4" style={{ alignItems: 'center' }}>
                        <div 
                            className="cursor-pointer"
                            onClick={() => handleCheckboxChange({ target: { name: 'exclude_FMP', checked: !formData.exclude_FMP } })}
                            style={{
                                width: '16px',
                                height: '16px',
                                borderRadius: '4px',
                                border: formData.exclude_FMP ? '1px solid #75BD2F' : '1px solid #cbc1b7',
                                backgroundColor: formData.exclude_FMP ? '#75BD2F' : '#f5f1ed',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: 0,
                                transition: 'all 0.3s ease'
                            }}
                        >
                            {formData.exclude_FMP && (
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="white" stroke="white" strokeWidth="1.5" />
                                </svg>
                            )}
                        </div>
                        <label 
                            onClick={() => handleCheckboxChange({ target: { name: 'exclude_FMP', checked: !formData.exclude_FMP } })}
                            style={{ fontSize: '14px', color: '#3E3E4A', fontFamily: 'Poppins, sans-serif', fontWeight: '500', margin: 0, cursor: 'pointer' }}
                        >
                            Excluded from Federal Medical Programs
                        </label>
                    </div>

                    <Grid item xs={12}>
                        <div style={{ height: '35px' }}></div>
                    </Grid>
                </Grid>
            </FormStyle>
        </FormModal>
    );
};

export default ServicePopup;
